import type { MouseEvent } from 'react';

import { useToast } from '@kcd/design-system';

import { useCurrentUser } from '~/domains/auth/queries';
import useAdultAuthDialogStore from '~/store/global/useAdultAuthDialogStore';

const ADULT_ONLY_TOAST_MESSAGE = '19세 이상 성인 회원만 이용할 수 있는 상품이에요.';

/**
 * MKPC-3616: 성인전용(19금) 카테고리 상품 && 성인 인증되지 않은 뷰어(미성년/미인증/비로그인)일 때 true.
 * 상품 카드 썸네일에 19 배지 + veil 을 띄울지 결정한다.
 * (성인 여부는 BE BuyerResponse.adultStatus, 상품 성인전용 여부는 SelectProductResponseV2.adultOnly)
 */
export function useShowAdultBadge(adultOnly?: boolean): boolean {
  const { user } = useCurrentUser();
  return !!adultOnly && user?.adultStatus !== 'ADULT';
}

/**
 * MKPC-3616: 19금 상품을 성인 인증되지 않은 뷰어가 클릭/담기 시도할 때 진입을 막는 가드.
 * - UNVERIFIED(생년월일 미상): 본인인증 안내 다이얼로그 노출
 * - 그 외(미성년 등): 토스트 노출
 * 카드/버튼 클릭 핸들러 첫 줄에서 `if (guardAdultClick(e)) return;` 형태로 사용한다.
 * 차단했으면 true(이동 막음), 아니면 false(정상 진행)를 반환한다.
 */
export function useAdultGuardClick(adultOnly?: boolean) {
  const { user } = useCurrentUser();
  const toast = useToast();
  const openAdultAuthDialog = useAdultAuthDialogStore((s) => s.open);

  return (e?: MouseEvent): boolean => {
    if (!adultOnly || user?.adultStatus === 'ADULT') return false;
    e?.preventDefault();
    e?.stopPropagation();

    if (user?.adultStatus === 'UNVERIFIED') {
      openAdultAuthDialog();
    } else {
      toast(ADULT_ONLY_TOAST_MESSAGE, { offset: 86 });
    }
    return true;
  };
}
